import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import DocumentHead from '../components/DocumentHead';
import { trackPartnerEvent } from '../hooks/useCandidaturaAnalytics';

const faq = [
  {
    q: 'Chi vende il prodotto al cliente finale?',
    a: 'Nel modello di intermediazione (ipotesi operativa attuale, da confermare col commercialista) il produttore è il soggetto della vendita: Mangitalia mette in contatto, gestisce catalogo, logistica e pagamento. La fattura dei prodotti al cliente, quando dovuta, è a carico del produttore secondo le indicazioni del commercialista.',
  },
  {
    q: 'Cosa fattura Mangitalia al produttore?',
    a: 'La piattaforma trattiene una commissione sul margine della vendita. Su quella commissione verrà emessa fattura elettronica (o documento equivalente) dalla piattaforma verso il produttore, una volta attivato il canale SDI. Oggi il sistema genera già una bozza interna di documento commissione.',
  },
  {
    q: 'Come funziona il pagamento?',
    a: 'Il cliente paga con Stripe al checkout. L’importo relativo al costo di produzione concordato viene accantonato nel portafoglio produttore e liberato alla consegna. I prelievi avvengono su richiesta verso IBAN (o, in futuro, Stripe Connect se attivato).',
  },
  {
    q: 'Devo emettere fattura elettronica?',
    a: 'Sì, se sei soggetto IVA e il commercialista lo richiede per le vendite B2B/B2C. Mangitalia raccoglie i tuoi dati fiscali (P.IVA, CF, codice destinatario, PEC FE) per facilitare lo scambio documentale. Non sostituisce il tuo software di fatturazione.',
  },
  {
    q: 'La ricevuta PDF della piattaforma è una fattura?',
    a: 'No. I PDF di Mangitalia sono documenti operativi (riepilogo ordine/pagamento). La fattura elettronica vera passa dallo SDI (Agenzia delle Entrate).',
  },
  {
    q: 'Ci sono quote di affiliazione o canoni mensili?',
    a: 'No. Non paghi per candidarti né un canone fisso di presenza. Il modello economico è legato alle vendite effettuate (commissione sul margine).',
  },
  {
    q: 'Cosa mi chiedete in candidatura / profilo?',
    a: 'Ragione sociale, Partita IVA, Codice fiscale, sede produttiva, codice destinatario SDI (7 caratteri) e PEC per fatturazione elettronica, più referenti e prodotti.',
  },
  {
    q: 'IVA sui prodotti alimentari: chi la gestisce?',
    a: 'Dipende dal modello fiscale scelto col commercialista e dalle aliquote del prodotto (4%, 10%, 22%). In piattaforma mostriamo stime IVA configurabili; le aliquote definitive e chi le applica vanno confermate professionalmente.',
  },
];

export default function ModelloBusiness() {
  return (
    <div className="min-h-screen bg-[var(--mang-bg,#FFFEFA)]">
      <DocumentHead
        title="Modello di business per partner"
        description="Come funziona Mangitalia per produttori e partner: pagamenti, commissioni, documenti fiscali e responsabilità."
        path="/per-partner"
      />

      <section className="mang-gradient text-white px-4 py-16 md:py-20">
        <div className="max-w-3xl mx-auto">
          <p className="text-xs uppercase tracking-widest opacity-90 mb-3">Per produttori e partner</p>
          <h1 className="text-3xl md:text-5xl font-bold mb-4 leading-tight">
            Come funziona il modello Mangitalia
          </h1>
          <p className="text-lg text-white/90 max-w-2xl">
            Brochure tecnica sul marketplace alimentare: chi vende, come si paga, cosa documentiamo
            e cosa resta a carico del produttore.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link
              to="/candidatura"
              onClick={() => trackPartnerEvent('partner_cta_click', { source: 'brochure_candidatura' })}
              className="bg-white text-[#c78244] px-6 py-3 rounded-full font-bold"
            >
              Candidati ora
            </Link>
            <Link
              to="/diventa-partner"
              className="border border-white/70 text-white px-6 py-3 rounded-full font-bold"
            >
              Scopri il percorso
            </Link>
          </div>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 py-12 space-y-8">
        <motion.div initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <h2 className="text-2xl font-bold text-[#c78244] mb-3">In sintesi</h2>
          <ul className="space-y-2 text-gray-700 list-disc pl-5">
            <li>Catalogo multi-categoria di eccellenze alimentari italiane selezionate.</li>
            <li>Cliente ordina e paga in piattaforma; logistica e SLA gestiti dal sistema.</li>
            <li>Produttore riceve l’accredito legato al costo di produzione concordato.</li>
            <li>Mangitalia trattiene la commissione di piattaforma sul margine.</li>
            <li>Documenti PDF = operativi; fattura elettronica = SDI (in attivazione).</li>
          </ul>
        </motion.div>

        <div className="bg-[#fff8ea] border border-[#f3deb8] rounded-2xl p-5 text-sm text-[#7a4a1f]">
          <strong>Nota importante.</strong> Il dettaglio fiscale (chi emette FE al cliente, aliquote IVA,
          timing fattura commissione) è soggetto a conferma del commercialista. Questa pagina descrive
          il modello di prodotto e ciò che la piattaforma supporta tecnicamente.
        </div>

        <div>
          <h2 className="text-2xl font-bold text-[#c78244] mb-6">Domande frequenti</h2>
          <div className="space-y-4">
            {faq.map((item) => (
              <details key={item.q} className="bg-white border border-[#f3deb8]/80 rounded-xl p-4 group">
                <summary className="font-semibold text-gray-800 cursor-pointer list-none flex justify-between gap-3">
                  {item.q}
                  <span className="text-[#c78244] group-open:rotate-45 transition">+</span>
                </summary>
                <p className="mt-3 text-gray-600 leading-relaxed text-sm">{item.a}</p>
              </details>
            ))}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-[#fff8ea] border border-[#f3deb8] rounded-2xl p-6"
        >
          <h2 className="text-xl font-bold text-[#c78244] mb-3">🤝 Invita altri produttori, guadagna sul loro venduto</h2>
          <p className="text-gray-700 text-sm leading-relaxed mb-4">
            Come produttore Mangitalia hai un <strong>codice amico personale</strong> nel tuo profilo.
            Se lo condividi con altri produttori e si candidano usando quel codice, per 12 mesi
            ricevi una percentuale su tutto il loro fatturato in piattaforma — automaticamente, senza
            doverci fare nulla.
          </p>
          <p className="text-gray-700 text-sm leading-relaxed mb-4">
            Non stai reclutando per conto di nessuno: stai semplicemente allargando una community
            di produttori artigianali italiani di qualità. E vieni premiato per averlo fatto.
          </p>
          <Link
            to="/diventa-closer"
            className="inline-block bg-[#c78244] text-white px-5 py-2.5 rounded-full font-semibold text-sm"
          >
            Scopri il programma inviti →
          </Link>
        </motion.div>

        <div className="flex flex-wrap gap-4 text-sm pt-4 border-t border-[#f3deb8]">
          <Link to="/termini" className="text-[#c78244] underline">Termini</Link>
          <Link to="/privacy" className="text-[#c78244] underline">Privacy</Link>
          <Link to="/presentazione" className="text-[#c78244] underline">Presentazione completa</Link>
          <Link to="/" className="text-gray-500 underline">Home</Link>
        </div>
      </section>
    </div>
  );
}
